// Quick Reorder view — pinned + most-ordered grid with prominent reorder
// CTA per card. Per spec §12.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { listDesigns, reorderDesign, forkDesign, patchDesign, dispatchCartAdd, StudioApiError } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useStudio } from '../../lib/store';
import { DesignCard } from '../../components/DesignCard';
import { SkeletonGrid } from '../../components/LoadingSkeleton';
import { EmptyState } from '../../components/EmptyState';

export function QuickReorderView() {
  const pushToast = useStudio((s) => s.pushToast);
  const setView = useStudio((s) => s.setView);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: qk.designs({ view: 'reorder' }),
    queryFn: () => listDesigns({ sort: 'most_ordered', pinned_first: true, limit: 12 }),
  });

  const reorderMut = useMutation({
    mutationFn: async (args: { id: string; qty?: number; variantId?: number }) => {
      const body: { quantity?: number; variant_id?: number } = {};
      if (args.qty != null) body.quantity = args.qty;
      if (args.variantId != null) body.variant_id = args.variantId;
      const desc = await reorderDesign(args.id, Object.keys(body).length ? body : undefined);
      return dispatchCartAdd(desc);
    },
    onSuccess: (url) => (window.location.href = url),
    onError: (err) => pushToast({ type: 'error', message: err instanceof StudioApiError ? err.message : 'Could not reorder' }),
  });

  const forkMut = useMutation({
    mutationFn: (id: string) => forkDesign(id),
    onSuccess: (resp) => (window.location.href = resp.editor_url),
    onError: () => pushToast({ type: 'error', message: 'Could not fork design' }),
  });

  // Pin toggle — flips is_pinned, then invalidates the reorder query so
  // the grid re-orders with pinned designs first per spec §12.
  const pinMut = useMutation({
    mutationFn: ({ id, isPinned }: { id: string; isPinned: boolean }) =>
      patchDesign(id, { is_pinned: !isPinned }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.designs({ view: 'reorder' }) });
      void qc.invalidateQueries({ queryKey: qk.designs({ sidebar: true }) });
    },
    onError: () => pushToast({ type: 'error', message: 'Could not update pin' }),
  });

  // Quick Reorder shows order-history-relevant designs:
  //   - Has been ordered at least once (rollup > 0), OR
  //   - Pinned by the customer (manually elevated)
  // Always exclude cancelled designs — reordering a cancelled order makes
  // no semantic sense; the customer should manually "Edit a copy" instead.
  const items = (query.data?.items ?? []).filter(
    (d) => (d.rollups.total_orders > 0 || d.is_pinned) && d.status !== 'cancelled',
  );

  return (
    <section aria-label="Quick Reorder">
      <header className="view-head">
        <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 8 }}>
          One tap, one click
        </p>
        <h1>Quick Reorder</h1>
        <p className="sub">
          Your favorite designs, ready to ship. Pick a quantity if you want to change it, or just tap the big button.
        </p>
      </header>

      {query.isLoading ? (
        <SkeletonGrid count={6} variant="reorder" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Zap}
          title="No reorder history yet."
          body="Once you place your first order, your favorite designs will live here for one-tap reordering."
          ctaLabel="See all my designs"
          onCta={() => setView('designs')}
        />
      ) : (
        <div className="reorder-grid">
          {items.map((d) => (
            <DesignCard
              key={d.id}
              design={d}
              variant="reorder"
              onReorder={(id, qty, variantId) => reorderMut.mutate({ id, qty, variantId })}
              onEditCopy={(id) => forkMut.mutate(id)}
              onPinToggle={(id) => pinMut.mutate({ id, isPinned: d.is_pinned })}
            />
          ))}
        </div>
      )}

      <style>{`
        .reorder-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }
      `}</style>
    </section>
  );
}
